import React from 'react';
import type { SpeciesTimberConfig } from '../../types';
import { roundProductionBoardFeet } from '../../utils/productionBoardFeet';
import './HardwareListTable.css';
import './ProductionBoardFeetOverride.css';
import './ProposalBreakdownGroups.css';

const GroupTitle: React.FC<{ label: string; extra?: React.ReactNode }> = ({ label, extra }) => (
  <div className="hardware-group-title">
    <span>{label}</span>
    {extra}
  </div>
);

interface ProductionBreakdownGroupsProps {
  boardFeet: number;
  boardFeetLabel: string;
  boardFeetReadOnly?: boolean;
  ratePerBdft: number;
  formatCurrency: (value: number) => string;
  disabled?: boolean;
  overrideEnabled: boolean;
  overrideDisabled?: boolean;
  onBoardFeetChange: (value: number) => void;
  onRateChange: (value: number) => void;
  onOverrideToggle: (enabled: boolean) => void;
  onBlur?: () => void;
  boardFeetRef?: React.Ref<HTMLInputElement>;
  groupRef?: React.Ref<HTMLDivElement>;
  alternateSpecies?: SpeciesTimberConfig[];
  altRateDrafts?: Record<string, string>;
  onAltRateDraft?: (speciesName: string, value: string) => void;
  onAltRateCommit?: (speciesName: string, value: string) => void;
  rateReadOnly?: boolean;
}

const ProductionBreakdownGroups: React.FC<ProductionBreakdownGroupsProps> = ({
  boardFeet,
  boardFeetLabel,
  boardFeetReadOnly,
  ratePerBdft,
  formatCurrency,
  disabled,
  overrideEnabled,
  overrideDisabled,
  onBoardFeetChange,
  onRateChange,
  onOverrideToggle,
  onBlur,
  boardFeetRef,
  groupRef,
  alternateSpecies,
  altRateDrafts,
  onAltRateDraft,
  onAltRateCommit,
  rateReadOnly,
}) => {
  const readonly = disabled ? ' hardware-table--readonly' : '';
  const alts = alternateSpecies || [];
  const shownBoardFeet = boardFeetReadOnly ? roundProductionBoardFeet(boardFeet) : boardFeet;

  return (
    <div className="timber-list-container hardware-list-wrap" ref={groupRef}>
      <div className="hardware-group" aria-label="Shop">
        <GroupTitle
          label="Shop"
          extra={(
            <label className={`production-bf-override-toggle ${overrideEnabled ? 'active' : ''} ${overrideDisabled ? 'production-bf-override-toggle--disabled' : ''}`}>
              <span className="production-bf-override-toggle-label">Override</span>
              <input
                type="checkbox"
                checked={overrideEnabled}
                disabled={overrideDisabled}
                onChange={(e) => onOverrideToggle(e.target.checked)}
              />
              <span className="production-bf-override-slider" aria-hidden />
            </label>
          )}
        />
        <table className={`hardware-table hardware-table--qty-cost${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Name</th>
              <th className="hardware-col-num">Bdft</th>
              <th className="hardware-col-num">$/bdft</th>
              <th className="hardware-col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="hardware-col-name">
                <span className="hardware-ref-text">{boardFeetLabel}</span>
              </td>
              <td className="hardware-col-num">
                <input
                  ref={boardFeetRef}
                  type="number"
                  min="0"
                  step="1"
                  readOnly={boardFeetReadOnly || disabled}
                  className={boardFeetReadOnly ? 'production-bf-linked' : undefined}
                  value={shownBoardFeet || ''}
                  onChange={(e) => onBoardFeetChange(parseFloat(e.target.value) || 0)}
                  onBlur={onBlur}
                />
              </td>
              <td className="hardware-col-num">
                {rateReadOnly ? (
                  <span>{formatCurrency(ratePerBdft)}</span>
                ) : (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={ratePerBdft || ''}
                    disabled={disabled}
                    onChange={(e) => onRateChange(parseFloat(e.target.value) || 0)}
                    onBlur={onBlur}
                  />
                )}
              </td>
              <td className="hardware-col-num">
                <span>{formatCurrency(shownBoardFeet * ratePerBdft)}</span>
              </td>
            </tr>
            {alts.map((alt) => {
              const draft = altRateDrafts?.[alt.speciesName];
              const rate =
                draft !== undefined
                  ? draft
                  : typeof alt.ratePerBdft === 'number' && Number.isFinite(alt.ratePerBdft)
                    ? String(alt.ratePerBdft)
                    : '';
              const numericRate = parseFloat(rate) || 0;
              return (
                <tr key={alt.speciesName}>
                  <td className="hardware-col-name">
                    <span className="hardware-ref-text">{alt.speciesName}</span>
                  </td>
                  <td className="hardware-col-num">
                    <span />
                  </td>
                  <td className="hardware-col-num">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Same as default"
                      value={rate}
                      disabled={disabled}
                      onChange={(e) => onAltRateDraft?.(alt.speciesName, e.target.value)}
                      onBlur={(e) => {
                        onAltRateCommit?.(alt.speciesName, e.target.value);
                        onBlur?.();
                      }}
                    />
                  </td>
                  <td className="hardware-col-num">
                    <span>{numericRate > 0 ? formatCurrency(shownBoardFeet * numericRate) : ''}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductionBreakdownGroups;
